import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const RouteMap = ({ stops, height = '250px' }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load Mapbox CDN
  useEffect(() => {
    if (window.mapboxgl) {
      setMapReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js';
    script.onload = () => {
      const link = document.createElement('link');
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
      setMapReady(true);
    };
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!mapReady || !mapContainer.current || !stops || stops.length < 2) {
      setLoading(false);
      return;
    }

    // Filter stops with valid coordinates
    const validStops = stops.filter(s => s.lat != null && s.lon != null);
    if (validStops.length < 2) {
      setLoading(false);
      return;
    }

    window.mapboxgl.accessToken = MAPBOX_TOKEN;

    const bounds = new window.mapboxgl.LngLatBounds();
    validStops.forEach(s => bounds.extend([s.lon, s.lat]));

    map.current = new window.mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      bounds: bounds,
      fitBoundsOptions: { padding: 50, maxZoom: 14 },
    });

    map.current.on('load', () => {
      setLoading(false);

      // Add route line
      const coordinates = validStops.map(s => [s.lon, s.lat]);
      map.current.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates },
        },
      });

      map.current.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#f97316',
          'line-width': 4,
          'line-dasharray': [2, 1],
        },
      });

      // Add markers
      validStops.forEach((stop, i) => {
        const isFirst = i === 0;
        const isLast = i === validStops.length - 1;

        const el = document.createElement('div');
        el.style.width = isFirst || isLast ? '28px' : '20px';
        el.style.height = isFirst || isLast ? '28px' : '20px';
        el.style.borderRadius = '50%';
        el.style.border = '3px solid white';
        el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.fontSize = '11px';
        el.style.fontWeight = 'bold';
        el.style.color = 'white';

        if (isFirst) {
          el.style.background = '#22c55e';
          el.textContent = 'A';
        } else if (isLast) {
          el.style.background = '#ef4444';
          el.textContent = String.fromCharCode(65 + i);
        } else {
          el.style.background = '#f97316';
          el.textContent = String.fromCharCode(65 + i);
        }

        const popup = new window.mapboxgl.Popup({ offset: 20, closeButton: false })
          .setText(stop.address);

        new window.mapboxgl.Marker(el)
          .setLngLat([stop.lon, stop.lat])
          .setPopup(popup)
          .addTo(map.current);
      });
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapReady, stops]);

  if (!stops || stops.length < 2) return null;

  return (
    <div className="relative rounded-lg overflow-hidden border border-zinc-200" style={{ height }} data-testid="route-map">
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-100">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      )}
    </div>
  );
};

export default RouteMap;
